import React, { useEffect, useState } from 'react';
import randomcolor from 'randomcolor';
import { Link } from 'react-router-dom';
import OrderLine from './OrderLine';
import OrderFields from './OrderFields';
import Address from './Address';
import AbcOrder from './AbcOrder';
import Switch from '../Switch';
import DeliveryForm from '../edi/DeliveryForm';
import { cbcmToCbm, inPallets, getSize } from '../../dimensions';
import { updateOrder, updateOrders, changeState } from '../../websocket';
import { splitLocation, stringifyLocation } from '../../helper';
import sortLocation from '../../locationSorter';
import { printStickers, updateAddresses } from '../../api';
import '../../css/planpage.css';
import { inWarehouse } from '../WarehouseForm';

const NO_LOCATIONS = { ONBEKEND: 0 };

function requiresNewStickers(abc) {
	return abc.some(order => {
		return order.lastPrinted && order.lastPalletUpdate && order.lastPrinted < order.lastPalletUpdate;
	});
}

function sortLocationLines(lhs, rhs) {
	const lCode = lhs.line.item.code;
	const rCode = rhs.line.item.code;
	if(lCode > rCode) {
		return 1;
	} else if(lCode < rCode) {
		return -1;
	}
	return 0;
}

function getLocations(line) {
	if(line.status !== 'NONE' && line.pickedFrom) {
		return line.pickedFrom;
	}
	return NO_LOCATIONS;
}

function PlanPage({ order, person, abc, warehouse, locations: warehouses }) {
	const [pallets, setPallets] = useState(order?.maxPallets || '');
	const [printing, setPrinting] = useState(false);
	useEffect(() => {
		setPallets(order?.maxPallets || '');
	}, [order?.maxPallets]);
	useEffect(() => {
		const items = {};
		abc.forEach(o => {
			o.lines.forEach(line => {
				if(!inWarehouse(warehouse, line, warehouses)) {
					return;
				}
				if(line.wmsRemark) {
					items[line.item.code] = line.wmsRemark;
				}
			});
		});
		const entries = Object.entries(items);
		if(entries.length) {
			window.alert(`LET OP!\n${entries.map(([sku, remark]) => `Item ${sku}: ${remark}`).join('\n')}`);
		}
	}, [order?._id]);
	const switches = [];
	if(['PICKER', 'TEAM_LEADER'].includes(person.role) && ['ON_PALLET', 'NONE'].includes(order.status)) {
		switches.push(<p>
			{order.dropship ? 'Gepakt' : 'Op pallet'} <Switch
				value={order.status === 'ON_PALLET'}
				disabled={!order.dropship && !order.noStickers && order.status !== 'ON_PALLET' && abc.every(o => !o.pallets?.length) && abc.some(o => o.lines.some(l => l.picked || l.status === 'SUCCESS'))}
				onChange={e => {
					const status = e.target.checked ? 'ON_PALLET' : 'NONE';
					let orders;
					if(e.target.checked && abc.length) {
						orders = abc.filter(o => o.pallets?.length || o.lines.some(line => line.status !== 'NONE'));
						if(!orders.length) {
							return;
						}
					} else {
						orders = abc;
					}
					updateOrders(orders.map(o => o._id), 'status', status);
				}} />
		</p>);
	}
	const hasPallets = abc.some(o => o.pallets.length);
	if(['ON_PALLET', 'SEALING'].includes(order.status)) {
		if(person.role === 'PACKAGER') {
			switches.push(<p>
				Automatisch aanmelden <Switch
					value={!order.noRegistration}
					onChange={e => {
						updateOrders(abc.map(o => o._id), 'noRegistration', !e.target.checked);
					}} />
			</p>);
			switches.push(<p>
				Ingepakt <Switch
					value={order.status === 'SEALING'}
					disabled={order.status === 'ON_PALLET' && !hasPallets}
					onChange={e => {
						const status = e.target.checked ? 'SEALING' : 'ON_PALLET';
						updateOrders(abc.map(o => o._id), 'status', status);
					}} />
			</p>);
		} else if(person.role === 'TEAM_LEADER' && !order.dropship) {
			switches.push(<p>
				Sealing <Switch
					value={order.status === 'SEALING'}
					onChange={e => {
						const status = e.target.checked ? 'SEALING' : 'ON_PALLET';
						updateOrders(abc.map(o => o._id), 'status', status);
					}} />
			</p>);
		}
	}
	const status = switches.length ? React.createElement('div', { className: 'order-status' }, ...switches) : null;
	const locations = {};
	const states = {
		NONE: 0,
		SUCCESS: 0,
		FAILED: 0
	};
	let size = 0;
	const notified = [];
	const lineLocations = new Set();
	const failedBomGroups = new Set();
	abc.forEach(o => {
		const cs = person.role === 'CUSTOMER_SERVICE';
		o.lines.forEach(line => {
			if(line.bomParent) {
				if(line.status === 'FAILED' || line.status === 'NONE' && !Object.keys(line.locations).length) {
					failedBomGroups.add(`${o._id}_${line.bomParent}`);
				}
			}
			if(warehouses[line.location]) {
				lineLocations.add(warehouses[line.location]);
			}
			const num = Object.keys(line.locations).length;
			if(num || cs || line.status !== 'NONE') {
				if(!inWarehouse(warehouse, line, warehouses)) {
					return;
				}
				states[line.status]++;
				const locs = num ? line.locations : getLocations(line);
				Object.keys(locs).forEach(key => {
					if(key !== 'INPAK' || num === 1) {
						if(!locations[key]) {
							locations[key] = [];
						}
						locations[key].push({ line, quantity: locs[key], order: o });
					}
				});
				size += getSize(line);
			}
		});
		if(o.lastInformed) {
			notified.push(new Date(o.lastInformed).toLocaleString());
		}
	});
	const csOrTl = ['CUSTOMER_SERVICE', 'TEAM_LEADER'].includes(person.role);
	const buttons = [];
	if(csOrTl) {
		buttons.push(<Link key="slip" to={`/order/${order._id}/slip`} className="btn btn-primary">Pakbon</Link>);
	}
	if(order.status !== 'NONE' && (order.noStickers || person.role === 'CUSTOMER_SERVICE' || hasPallets)) {
		if(csOrTl && (order.status === 'ON_PALLET' || order.status === 'SEALING')) {
			buttons.push(' ', <Link key="stickers" to={`/order/${order._id}/stickers`} className="btn btn-primary">Stickers</Link>);
		}
		if(states.NONE) {
			buttons.push(<br />, <span className="text-danger">Niet alles is gepakt</span>);
		}
	}
	if(order.status !== 'DONE' && person.role === 'CUSTOMER_SERVICE') {
		buttons.unshift(<button key="updateAddress" className="btn btn-primary" onClick={e => {
			e.preventDefault();
			updateAddresses(abc.map(o => o._id)).then(() => {
				window.alert('Gesynchroniseerd met NAV');
			}).catch(err => {
				console.log('Failed to update address', err);
			});
		}}>Sync adres</button>, ' ');
	}
	const creators = csOrTl ? abc.map(({ creator }) => creator).filter(c => c) : [];
	let prev;
	return <section className={order.dropship ? 'dropship-order container' : 'container'}>
		<div>
			{order.dropship && person.role !== 'CUSTOMER_SERVICE' && person.role !== 'PACKAGER' ? <h1>Dropship order</h1> : <>
				<h1>{abc.map(({ code }) => code).join(' ')}</h1>
				{order.dropship ? <p>Dropship order</p>
					: order.noStickers && <p>B2C of zelf-ophaalorder</p>}
			</>}
			{notified.length ? <p>Laatst naar de klant gestuurd op {notified.join(', ')}</p> : null}
			{requiresNewStickers(abc) && <p>Er moeten nieuwe stickers worden geplakt voor deze order.</p>}
			{creators.length > 0 && <p>Toegevoegd door: {[...new Set(creators.map(c => c.name))].join(', ')}</p>}
			<div className="row">
				<div className="col-4">
					{buttons.length > 0 && <p>
						{buttons}
					</p>}
					{csOrTl && <label className="d-block">
						Pakketdienst: <input type="checkbox" checked={order.dropship} onChange={e => {
							updateOrder(order._id, 'dropship', e.target.checked);
						}} />
					</label>}
					{person.role === 'CUSTOMER_SERVICE' && !order.dropship && <label>
						Max. pallets: <input
							type="number"
							className="form-control"
							min="1"
							value={pallets}
							onChange={e => {
								let { value } = e.target;
								setPallets(value);
								if(value === '') {
									value = null;
								} else {
									value = parseInt(value, 10);
								}
								if(order.maxPallets !== value && (value === null || !isNaN(value))) {
									updateOrder(order._id, 'maxPallets', value);
								}
							}} />
					</label>}
				</div>
				{csOrTl && <div className="col-8">
					{(!order.dropship || abc.length === 1) && <Address order={order} className="text-right" />}
				</div>}
			</div>
			{person.role === 'PACKAGER' && <div className="row">
				<div className="col-4">
					{order.status === 'SEALING' && <button className="btn btn-primary" onClick={async e => {
						e.preventDefault();
						setPrinting(true);
						try {
							await printStickers(order._id);
							window.alert('Printopdracht verstuurd');
						} catch(err) {
							console.log('Failed to print stickers', err);
						} finally {
							setPrinting(false);
						}
					}} disabled={printing}>Print stickers</button>}
				</div>
				<div className="col-8">
					<p className="text-right">{order.customer.code} {order.customer.name}</p>
					<Address order={order} className="text-right" />
				</div>
			</div>}
			{person.role === 'CUSTOMER_SERVICE' && !states.FAILED && <p>
				Alles gepakt <Switch
					value={!states.NONE || order.lines.every(line => {
						if(Object.keys(line.locations).some(key => line.locations[key] >= line.quantity)) {
							return line.status === 'SUCCESS';
						}
						return true;
					})}
					onChange={e => {
						const newStatus = e.target.checked ? 'SUCCESS' : 'NONE';
						order.lines.forEach(line => {
							if(!inWarehouse(warehouse, line)) {
								return;
							}
							const location = Object.keys(line.locations).find(key => line.locations[key] >= line.quantity);
							if(location) {
								changeState(order._id, line._id, location, newStatus);
							}
						});
					}} />
			</p>}
			<div className="row">
				<div className="col-6"><p>
					{person.role !== 'PACKAGER' && <>{cbcmToCbm(size)} m<sup>3</sup>, {inPallets(size)} pallet(s)<br /></>}
					Warehouse: <strong>{[...lineLocations].join(' ')}</strong>
				</p></div>
				<div className="col-6">{status}</div>
			</div>
			{order.status !== 'DONE' && person.role === 'CUSTOMER_SERVICE' && !order.dropship && <DeliveryForm
				order={order} abc={abc} post={submitting => <p>
					Let op: de delivery option staat op de stickers!{' '}
					<input type="submit" className="btn btn-primary" value="Save" disabled={submitting} />
				</p>} />}
			<OrderFields order={order} abc={abc} readonly={order.dropship ? person.role !== 'PACKAGER' || order.status !== 'ON_PALLET' : person.role !== 'PICKER' || order.status !== 'NONE' && order.status !== 'ON_PALLET'}>
				<ol className="list-unstyled">
					{Object.keys(locations).map(splitLocation).sort(sortLocation).map(place => {
						const seed = `${place.loods}`;
						const classes = ['order-line'];
						if(seed !== prev) {
							classes.push('first');
						}
						prev = seed;
						const locationLines = locations[place.location].sort(sortLocationLines);
						return <li key={place.location} style={{ borderColor: randomcolor({ seed }) }} className={classes.join(' ')}>
							{locationLines.map(({ line, quantity, order: o }) => <OrderLine
								key={`${place.location}_${line._id}_${o._id}`}
								role={person.role}
								order={o}
								quantity={quantity}
								place={place.location}
								location={stringifyLocation(place)}
								bomFailed={failedBomGroups.has(`${o._id}_${line.bomParent}`)}
								line={line} />)}
						</li>;
					})}
				</ol>
				{status}
			</OrderFields>
		</div>
	</section>;
}

export default function Page(props) {
	return <AbcOrder {...props} component={PlanPage} />;
}
